import React, { useMemo } from "react";
import { Input } from "domains/3d/interfaces";
import { useAssetsByIds } from "domains/assets/hooks/useAssetsByIds";
import { formatNumber } from "domains/commons/misc";
import { FileImageType } from "domains/file-manager/interfaces";
import {
  AsideList,
  AsideListItem,
  AsideSection,
  AsideTitledData,
  AsideTitledText,
} from "domains/ui/components/Aside";
import {
  GetJobIdApiResponse,
  GetModelsByModelIdApiResponse,
} from "infra/api/generated/api";
import _ from "lodash";

import { ParametersPromptDisplay } from "./SectionParameters";
import { ImageGrid } from "./SectionReference";
import { MultipleRefImages } from "./SectionRefs";

export interface AsideSectionParametersCustomProps {
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  job: GetJobIdApiResponse["job"] | undefined;
  section: "top" | "parameters";
}

export default React.memo(function AsideSectionParametersCustom({
  model,
  job,
  section,
}: AsideSectionParametersCustomProps) {
  const groups = useMemo(() => {
    if (!model?.inputs) {
      return [];
    }

    const triggerPosition = model.uiConfig?.triggerGenerate?.after;
    const triggerIndex = triggerPosition
      ? model.inputs?.findIndex((input) => input.name === triggerPosition)
      : -1;
    let ungroupedInputs: Input[] = [];
    if (triggerIndex === undefined || triggerIndex === -1) {
      ungroupedInputs = section === "top" ? model.inputs : [];
    } else {
      ungroupedInputs =
        section === "top"
          ? model.inputs.slice(0, triggerIndex + 1)
          : model.inputs.slice(triggerIndex + 1);
    }
    const groupedInputs: {
      title: string;
      inputs: Input[];
    }[] = [];
    ungroupedInputs.forEach((input) => {
      const group = input.group;
      if (group) {
        const groupIndex = groupedInputs.findIndex((g) => g.title === group);
        if (groupIndex === -1) {
          groupedInputs.push({
            title: group,
            inputs: [input],
          });
        } else {
          groupedInputs[groupIndex].inputs.push(input);
        }
      } else {
        const lastGroup = groupedInputs[groupedInputs.length - 1];
        if (lastGroup && !lastGroup.title) {
          lastGroup.inputs.push(input);
        } else {
          groupedInputs.push({
            title: "",
            inputs: [input],
          });
        }
      }
    });
    return groupedInputs;
  }, [model, section]);

  const assetIds = useMemo(() => {
    const ids = [];
    const inputs = groups.flatMap((group) => group.inputs);
    for (const input of inputs ?? []) {
      if (input.type === "file" || input.type === "file_array") {
        const value = job?.metadata.input?.[input.name];
        if (value && typeof value === "string") {
          ids.push(value);
        } else if (Array.isArray(value)) {
          ids.push(...value.filter((v) => !!v));
        }
      }
    }
    return ids;
  }, [groups, job]);

  const { assetsByIds } = useAssetsByIds({ assetIds });

  const fields = _.compact(
    groups?.map((group) => {
      const children = group.inputs.map((input) => {
        const value = job?.metadata.input?.[input.name];
        return (
          <ParameterItem
            key={input.name}
            input={input}
            value={value}
            section={section}
            assetsByIds={assetsByIds}
          />
        );
      });
      if (group.title) {
        return (
          <AsideTitledData key={group.title} title={group.title}>
            {children}
          </AsideTitledData>
        );
      }
      return children;
    })
  );

  if (!fields.length) {
    return null;
  }

  if (section === "parameters") {
    return (
      <AsideSection id="parameters" title="Parameters">
        <AsideList>{fields}</AsideList>
      </AsideSection>
    );
  }

  return <>{fields}</>;
});

// ----------------------------------

interface ParameterItemProps {
  input: Input;
  value: string;
  section: "top" | "parameters";
  assetsByIds: {
    [id: string]: FileImageType | undefined;
  };
}

function ParameterItem({
  input,
  value,
  section,
  assetsByIds,
}: ParameterItemProps) {
  const label = input.label ?? input.name;

  if (input.name === "prompt") {
    return null;
  }

  if (input.prompt && value !== undefined) {
    if (section === "top") {
      return <AsideTitledText title={label} value={value} copyable />;
    }
    return <ParametersPromptDisplay title={label} value={value} copyable />;
  }

  if (input.type === "string" && value !== undefined) {
    return <AsideListItem label={label} value={value} />;
  }

  if (input.type === "number" && value !== undefined) {
    return (
      <AsideListItem
        label={label}
        value={typeof value === "number" ? formatNumber(value) : "-"}
      />
    );
  }

  if (input.type === "boolean") {
    return <AsideListItem label={label} value={value ? "Yes" : "No"} />;
  }

  if (input.type === "file" && value !== undefined && assetsByIds[value]) {
    return (
      <AsideList variant="70-30">
        <AsideListItem label={label} />
        <ImageGrid image={assetsByIds[value].meta} label={label} />
      </AsideList>
    );
  }

  if (input.type === "file_array" && Array.isArray(value) && value.length) {
    return (
      <AsideList variant="70-30">
        <AsideListItem label={label} />
        <MultipleRefImages
          assets={_.compact(value.map((id) => assetsByIds[id]?.meta))}
        />
      </AsideList>
    );
  }

  return null;
}
